import moment from 'moment';
import { actionConstants } from 'redux-layer/constants';
import {
  startReqActionType,
  successReqActionType,
  failReqActionType,
  authHeader,
  apiHeader,
  processResponse
} from 'helper/requestHelper';
import { getEnvironment, convertUTCtoLocal } from 'helper/utils';
import {
  isVideoOption,
  isImageOption,
  SUPPORTED_IMAGE_FORMATS,
  IMAGE_MIME_TYPES
} from 'helper/constants';

const extractAssetArray = result => {
  if (!result) return [];
  if (Array.isArray(result)) return result;
  if (Array.isArray(result.data)) return result.data;
  if (Array.isArray(result.Items)) return result.Items;
  return [];
};

const isVideoAsset = asset => {
  if (!asset) return false;

  // Exclude uploads
  const appName = (asset.app_name || '').toLowerCase();
  const option = (asset.args?.option || asset.option || '').toLowerCase();
  const tool = (asset.tool || '').toLowerCase();
  if (
    appName.includes('upload') ||
    option.includes('upload') ||
    tool.includes('upload')
  ) {
    return false;
  }

  // Check if option/app_name/tool/service is a known video option or matches video keywords
  const rawOption =
    asset.args?.option ||
    asset.option ||
    asset.tool ||
    asset.service_name ||
    asset.app_name ||
    '';

  if (isVideoOption(rawOption)) {
    return true;
  }

  // Check mime_type
  const mimeType = (asset.mime_type || '').toLowerCase();
  if (mimeType.startsWith('video/')) {
    return true;
  }

  // Check app_name
  if (appName.includes('video')) {
    return true;
  }

  // Check if results has mp4
  if (
    Array.isArray(asset.results) &&
    asset.results.some(
      r => typeof r === 'string' && r.toLowerCase().endsWith('.mp4')
    )
  ) {
    return true;
  }

  return false;
};

const isImageAsset = asset => {
  if (!asset) return false;

  // Exclude uploads
  const appName = (asset.app_name || '').toLowerCase();
  const option = (asset.args?.option || asset.option || '').toLowerCase();
  const tool = (asset.tool || '').toLowerCase();
  if (
    appName.includes('upload') ||
    option.includes('upload') ||
    tool.includes('upload')
  ) {
    return false;
  }

  // Video assets are handled separately
  if (isVideoAsset(asset)) {
    return false;
  }

  // Exclude audio assets
  const mimeType = (asset.mime_type || '').toLowerCase();
  if (
    mimeType.startsWith('audio/') ||
    appName.includes('audio') ||
    appName.includes('sound') ||
    appName.includes('voice') ||
    appName.includes('music') ||
    option.includes('audio') ||
    option.includes('sound') ||
    option.includes('voice') ||
    option.includes('music') ||
    tool.includes('audio')
  ) {
    return false;
  }

  // Check known image option or substring
  const rawOption =
    asset.args?.option ||
    asset.option ||
    asset.tool ||
    asset.service_name ||
    asset.app_name ||
    '';

  if (isImageOption(rawOption)) {
    return true;
  }

  // Check mime_type
  if (mimeType.startsWith('image/') || IMAGE_MIME_TYPES.includes(mimeType)) {
    return true;
  }

  // Check results for image extensions
  if (
    Array.isArray(asset.results) &&
    asset.results.some(
      r =>
        typeof r === 'string' &&
        SUPPORTED_IMAGE_FORMATS.some(ext => r.toLowerCase().endsWith(`.${ext}`))
    )
  ) {
    return true;
  }

  // Check app_name
  if (appName.includes('image')) {
    return true;
  }

  return false;
};

const inferAssetProvider = (asset, option) => {
  const prov = asset.provider;
  if (
    prov &&
    prov !== 'Vimmerse' &&
    prov !== 'Asset Video' &&
    prov !== 'Asset Image'
  ) {
    return prov;
  }
  const checkStr = `${option || ''} ${asset.tool || ''} ${
    asset.service_name || ''
  } ${asset.app_name || ''}`.toLowerCase();
  if (checkStr.includes('googleomni') || checkStr.includes('omni')) {
    return 'GoogleOmni';
  }
  if (checkStr.includes('kling')) return 'KlingAI';
  if (checkStr.includes('minimax') || checkStr.includes('hailuo')) {
    return 'MinimaxHailuo';
  }
  if (checkStr.includes('hunyuan')) return 'Hunyuan';
  if (checkStr.includes('wan')) return 'Wan';
  if (checkStr.includes('pixverse')) return 'Pixverse';
  if (checkStr.includes('veo')) return 'Veo';
  if (checkStr.includes('sora')) return 'Sora';
  if (checkStr.includes('vidu')) return 'Vidu';
  if (checkStr.includes('seedance')) return 'Seedance';
  if (checkStr.includes('happyhorse')) return 'HappyHorse';
  if (checkStr.includes('luma')) return 'LumaAI';
  if (checkStr.includes('runway')) return 'RunwayML';
  if (checkStr.includes('pvideo')) return 'PVideo';
  if (checkStr.includes('grok')) return 'Grok';
  if (checkStr.includes('flux')) return 'Flux';
  if (checkStr.includes('sdxl') || checkStr.includes('stability')) {
    return 'StabilityAI';
  }
  if (checkStr.includes('midjourney')) return 'Midjourney';
  if (checkStr.includes('dall-e') || checkStr.includes('dalle')) return 'OpenAI';
  if (checkStr.includes('imagen')) return 'GoogleImagen';
  if (checkStr.includes('ideogram')) return 'Ideogram';
  if (checkStr.includes('recraft')) return 'Recraft';
  if (checkStr.includes('bria')) return 'Bria';
  if (prov) return prov;
  if (asset.app_name && !asset.app_name.toLowerCase().includes('asset')) {
    return asset.app_name;
  }
  return 'Assets';
};

const mapAssetToAIUsage = (asset, isImage = false) => {
  const option =
    asset.args?.option ||
    asset.option ||
    asset.tool ||
    asset.service_name ||
    asset.app_name ||
    (isImage ? 'Asset Image' : 'Asset Video');
  const provider = inferAssetProvider(asset, option);

  let duration = Number(asset.duration);
  let processingTime = Number(asset.processing_time);
  if (
    (!duration || isNaN(duration) || duration <= 0) &&
    asset.created_at &&
    asset.updated_at
  ) {
    const diffSec = moment(asset.updated_at).diff(
      moment(asset.created_at),
      'seconds',
      true
    );
    if (diffSec > 0) {
      duration = Math.round(diffSec * 100) / 100;
      processingTime = Math.round(diffSec * 100) / 100;
    }
  }

  return {
    ...asset,
    id: asset.id,
    asset_id: asset.id,
    media_id: null,
    is_asset: true,
    is_image: isImage,
    asset_type: isImage ? 'image' : 'video',
    status: asset.status,
    created_at: asset.created_at,
    updated_at: asset.updated_at,
    requested_at: asset.created_at,
    finished_at: asset.updated_at,
    duration: duration || 0,
    processing_time: processingTime || 0,
    motion_type: option,
    option: option,
    provider: provider
  };
};

export const isWithinOneHour = item => {
  if (!item) return false;
  const now = moment();
  const startTime = item.requested_at || item.created_at || item.request_time;
  const endTime = item.finished_at || item.updated_at || item.finish_time;

  let hasTimestamp = false;

  if (startTime) {
    hasTimestamp = true;
    const diffStart = now.diff(convertUTCtoLocal(startTime), 'minutes', true);
    if (diffStart >= -1 && diffStart <= 60) {
      return true;
    }
  }

  if (endTime) {
    hasTimestamp = true;
    const diffEnd = now.diff(convertUTCtoLocal(endTime), 'minutes', true);
    if (diffEnd >= -1 && diffEnd <= 60) {
      return true;
    }
  }

  // Allow items without any timestamp only if they come from the dedicated recent endpoint
  if (!hasTimestamp && !item.asset_id && !item.is_asset) {
    return true;
  }

  return false;
};

export const getAIRecentHistory = () => async dispatch => {
  dispatch({ type: startReqActionType(actionConstants.GET_AI_RECENT_USAGE) });
  try {
    const [
      mediaResult,
      assetsResult,
      processingAssetsResult,
      requestedAssetsResult
    ] = await Promise.allSettled([
      fetch(`${getEnvironment().API_URL}statistics/ai-tools/recent`, {
        method: 'GET',
        headers: authHeader()
      }).then(processResponse),
      fetch(`${getEnvironment().API_URL}assets?limit=100`, {
        method: 'GET',
        headers: apiHeader()
      }).then(processResponse),
      fetch(`${getEnvironment().API_URL}assets?limit=50&status=processing`, {
        method: 'GET',
        headers: apiHeader()
      }).then(processResponse),
      fetch(`${getEnvironment().API_URL}assets?limit=50&status=requested`, {
        method: 'GET',
        headers: apiHeader()
      }).then(processResponse)
    ]);

    const mediaItems =
      mediaResult.status === 'fulfilled' && Array.isArray(mediaResult.value)
        ? mediaResult.value
        : [];

    const assetMap = new Map();
    [assetsResult, processingAssetsResult, requestedAssetsResult].forEach(
      res => {
        if (res.status === 'fulfilled') {
          extractAssetArray(res.value).forEach(asset => {
            if (asset && asset.id) {
              assetMap.set(asset.id, asset);
            }
          });
        }
      }
    );
    const rawAssets = Array.from(assetMap.values());

    const validMedia = mediaItems.filter(isWithinOneHour);
    const validVideoAssets = rawAssets
      .filter(asset => isVideoAsset(asset) && isWithinOneHour(asset))
      .map(asset => mapAssetToAIUsage(asset, false));
    const validImageAssets = rawAssets
      .filter(asset => isImageAsset(asset) && isWithinOneHour(asset))
      .map(asset => mapAssetToAIUsage(asset, true));

    const merged = [
      ...validMedia,
      ...validVideoAssets,
      ...validImageAssets
    ].sort((a, b) => {
      const timeA = moment(a.requested_at || a.created_at || 0).valueOf();
      const timeB = moment(b.requested_at || b.created_at || 0).valueOf();
      return timeB - timeA;
    });

    dispatch({
      type: successReqActionType(actionConstants.GET_AI_RECENT_USAGE),
      payload: merged
    });
  } catch (error) {
    dispatch({ type: failReqActionType(actionConstants.GET_AI_RECENT_USAGE) });
  }
};


export const getStatistics = (start_date, end_date) => async dispatch => {
  dispatch({ type: startReqActionType(actionConstants.GET_STATISTICS) });
  try {
    const requestOptions = {
      method: 'GET',
      headers: authHeader()
    };

    const data = await fetch(
      `${getEnvironment().API_URL}statistics/${start_date}/${end_date}`,
      requestOptions
    );
    const json = await data.json();
    // Handle response structure: {data: [...], warnings: {...}}
    const statisticsData = json.data || [];
    const warnings = json.warnings || null;

    dispatch({
      type: successReqActionType(actionConstants.GET_STATISTICS),
      payload: statisticsData,
      warnings: warnings
    });
  } catch (error) {
    dispatch({ type: failReqActionType(actionConstants.GET_STATISTICS) });
  }
};

export const getAIRecentFailures = () => async dispatch => {
  dispatch({
    type: startReqActionType(actionConstants.GET_AI_RECENT_FAILURES)
  });
  try {
    const [mediaResult, failedAssetsResult, nsfwAssetsResult] =
      await Promise.allSettled([
        fetch(
          `${getEnvironment().API_URL}statistics/ai-tools/recent-failures`,
          {
            method: 'GET',
            headers: authHeader()
          }
        ).then(processResponse),
        fetch(`${getEnvironment().API_URL}assets?limit=100&status=failed`, {
          method: 'GET',
          headers: apiHeader()
        }).then(processResponse),
        fetch(`${getEnvironment().API_URL}assets?limit=50&status=nsfw`, {
          method: 'GET',
          headers: apiHeader()
        }).then(processResponse)
      ]);

    const mediaFailures =
      mediaResult.status === 'fulfilled' && Array.isArray(mediaResult.value)
        ? mediaResult.value
        : [];

    const rawFailed =
      failedAssetsResult.status === 'fulfilled'
        ? extractAssetArray(failedAssetsResult.value)
        : [];
    const rawNsfw =
      nsfwAssetsResult.status === 'fulfilled'
        ? extractAssetArray(nsfwAssetsResult.value)
        : [];

    const assetMap = new Map();
    [...rawFailed, ...rawNsfw].forEach(asset => {
      if (asset?.id && !assetMap.has(asset.id)) {
        assetMap.set(asset.id, asset);
      }
    });

    const now = moment();
    const assetFailures = Array.from(assetMap.values())
      .filter(asset => {
        const isVid = isVideoAsset(asset);
        const isImg = isImageAsset(asset);
        if (!isVid && !isImg) return false;
        const timestamp = asset.updated_at || asset.created_at;
        if (!timestamp) return false;
        const daysDiff = now.diff(convertUTCtoLocal(timestamp), 'days', true);
        return daysDiff >= 0 && daysDiff <= 7;
      })
      .map(asset => mapAssetToAIUsage(asset, isImageAsset(asset)));

    const merged = [...mediaFailures, ...assetFailures].sort((a, b) => {
      const timeA = moment(a.requested_at || a.created_at || 0).valueOf();
      const timeB = moment(b.requested_at || b.created_at || 0).valueOf();
      return timeB - timeA;
    });

    dispatch({
      type: successReqActionType(actionConstants.GET_AI_RECENT_FAILURES),
      payload: merged
    });
  } catch (error) {
    dispatch({
      type: failReqActionType(actionConstants.GET_AI_RECENT_FAILURES)
    });
  }
};
